<template>
  <!--
    Absent on the landlord domain, which has no tenant to resolve a desk against.
    For every tenant it is always here: the support keys carry Twine's own desk as
    their registered default, so there is no state where this has nothing to open.
  -->
  <div
    v-if="supportContact"
    class="relative"
  >
    <TwnBaseBtn
      v-if="hasTrigger"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      aria-label="Support contact"
      icon="help"
      label="Support"
      severity="secondary"
      size="small"
      variant="outlined"
      @click="togglePopover"
    />

    <TwnPopover
      ref="popoverRef"
      @hide="handleHide"
      @show="handleShow"
    >
      <!-- Bounded to the viewport so the panel still fits at 320px, where a fixed 20rem would clip. -->
      <div class="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-4">
        <header class="flex flex-col gap-0.5">
          <p class="text-sm font-medium text-surface-900">Support</p>
          <p class="text-xs text-surface-500">Who to contact when something goes wrong.</p>
        </header>

        <TwnSupportContactCard
          :support-contact="supportContact"
          compact
        />
      </div>
    </TwnPopover>
  </div>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef } from 'vue'

import type { TwnSupportContactIndicatorProps } from '@/domains/tenant/components/molecules/twn-support-contact-indicator/TwnSupportContactIndicator.types'

import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import TwnPopover from '@/components/atoms/prime/popover/twn-popover/TwnPopover.vue'
import TwnSupportContactCard from '@/domains/tenant/components/molecules/twn-support-contact-card/TwnSupportContactCard.vue'
import { useSupportContact } from '@/domains/tenant/composables/use-support-contact/useSupportContact'

// Without its button, a caller such as the sidebar opens the panel through `open`.
withDefaults(defineProps<TwnSupportContactIndicatorProps>(), { hasTrigger: true })

const supportContact = useSupportContact()

const popoverRef = useTemplateRef<InstanceType<typeof TwnPopover>>('popoverRef')

const isOpen = ref(false)

// `isOpen` follows the popover's own events rather than the click, so Escape and
// outside-click dismissals keep aria-expanded honest.
const togglePopover = (event: Event) => {
  popoverRef.value?.toggle(event)
}

const open = (event: Event) => popoverRef.value?.show(event)

const handleShow = () => {
  isOpen.value = true
}

const handleHide = () => {
  isOpen.value = false
}

defineExpose({ open })
</script>
